import ChildTable from "@/components/BaseTable/ChildTable";
import CustomButton from "@/components/common/Button";
import CommonModal from "@/components/common/CommonModal";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import SchemaFormView from "@/components/Schema/SchemaFormView";
import { capitalizeText } from "@/lib/capitalizeText";
import { generateTimestamp } from "@/lib/generateTimestamp";
import { SchemaService } from "@/services/APIs";
import {
  CustomChildTableProps,
  ModalState,
  TableRecord,
} from "@/utils/types/baseTable";
import { ProColumns } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import { Dropdown, MenuProps, Popconfirm, message } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Constants
const DEFAULT_PAGE_SIZE = 10;
const MODAL_WIDTH = 800;

// Utility functions
const generateUniqueId = (): number => Math.floor(Math.random() * 1e12);

const parseSchemaJson = (schemaInfo: any) => {
  if (!schemaInfo) {
    return null;
  }

  try {
    return schemaInfo.json;
  } catch (error: any) {
    console.error("Error parsing schema JSON:", error);
  }
};

// Simple utility to extract only individual record fields (non-array fields)
// This function works with multiple child tables by excluding the current child table field
const extractIndividualRecordData = (
  formData: any,
  childTableFieldName?: string
): any => {
  const individualRecordData: any = {};

  // Only include fields that are not arrays (child tables)
  Object.keys(formData).forEach(fieldName => {
    const fieldValue = formData[fieldName];
    // Skip the current child table field and any other array fields
    if (!Array.isArray(fieldValue) && fieldName !== childTableFieldName) {
      individualRecordData[fieldName] = fieldValue;
    }
  });

  return individualRecordData;
};

// Custom hooks
const useTableData = (value?: any[]) => {
  const [tableData, setTableData] = useState<TableRecord[]>(value ?? []);

  useEffect(() => {
    if (value !== undefined) {
      setTableData(value);
    }
  }, [value]);

  return [tableData, setTableData] as const;
};

const useModalState = () => {
  const [modalState, setModalState] = useState<ModalState>({
    open: false,
    editingId: null,
    isEditMode: false,
  });

  const openModal = useCallback((editingId: string | null = null) => {
    setModalState({
      open: true,
      editingId,
      isEditMode: !!editingId,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({
      open: false,
      editingId: null,
      isEditMode: false,
    });
  }, []);

  return { modalState, openModal, closeModal };
};

const useFormManagement = () => {
  const [form, setForm] = useState(() => createForm({}));

  const createNewForm = useCallback((initialValues?: any) => {
    const newForm = createForm(initialValues ? { initialValues } : {});
    setForm(newForm);
    return newForm;
  }, []);

  return { form, createNewForm };
};

const useTableOperations = (
  tableData: TableRecord[],
  updateTableData: (data: TableRecord[]) => void,
  closeModal: () => void,
  createNewForm: (initialValues?: any) => any,
  childTableFieldName?: string
) => {
  const validateFormData = useCallback((formData: any): boolean => {
    if (!formData || Object.keys(formData).length === 0) {
      message.warning("Please fill in the required fields");
      return false;
    }
    return true;
  }, []);

  const handleSave = useCallback(
    async (form: any) => {
      try {
        const formData = form.getFormState().values;
        if (!validateFormData(formData)) {
          return;
        }

        // Extract only the individual record fields, excluding child table fields
        const individualRecordData = extractIndividualRecordData(
          formData,
          childTableFieldName
        );

        const currentTimestamp = generateTimestamp();
        const newRecord: TableRecord = {
          ...individualRecordData,
          index: generateUniqueId(),
          created_on: currentTimestamp,
          updated_on: currentTimestamp,
        };

        updateTableData([...tableData, newRecord]);
        closeModal();
        message.success("Record saved successfully");
      } catch (error: any) {
        message.error("Failed to save record");
        console.error("Failed to save record:", error);
      }
    },
    [
      tableData,
      updateTableData,
      closeModal,
      validateFormData,
      childTableFieldName,
    ]
  );

  const handleUpdate = useCallback(
    async (form: any, editingId: string) => {
      try {
        const formData = form.getFormState().values;
        if (!validateFormData(formData)) {
          return;
        }

        // Extract only the individual record fields, excluding child table fields
        const individualRecordData = extractIndividualRecordData(
          formData,
          childTableFieldName
        );

        const currentTimestamp = generateTimestamp();
        const updatedData = tableData.map(item =>
          item.index === editingId
            ? {
                ...item,
                ...individualRecordData,
                updated_on: currentTimestamp,
              }
            : item
        );

        updateTableData(updatedData);
        closeModal();
        message.success("Record updated successfully");
      } catch (error: any) {
        message.error("Failed to update record");
        console.error("Failed to update record:", error);
      }
    },
    [
      tableData,
      updateTableData,
      closeModal,
      validateFormData,
      childTableFieldName,
    ]
  );

  const handleDelete = useCallback(
    (index: string) => {
      const newData = tableData.filter(item => item.index !== index);
      updateTableData(newData);
      message.success("Record deleted successfully");
    },
    [tableData, updateTableData]
  );

  const handleBulkDelete = useCallback(
    async (selectedRowKeys: React.Key[]) => {
      if (selectedRowKeys.length === 0) {
        message.warning("No items selected for deletion");
        return;
      }

      try {
        const newData = tableData.filter(
          item => !selectedRowKeys.includes(item.index)
        );
        updateTableData(newData);
        message.success(
          `${selectedRowKeys.length} record(s) deleted successfully`
        );
        return newData;
      } catch (error: any) {
        message.error("Failed to delete records");
        console.error("Failed to delete records:", error);
      }
    },
    [tableData, updateTableData]
  );

  return {
    handleSave,
    handleUpdate,
    handleDelete,
    handleBulkDelete,
  };
};

export const CustomChildTable = (props: CustomChildTableProps) => {
  const { x_child, onTableDataChange, value, onChange } = props;

  // State management
  const [tableData, setTableData] = useTableData(value);
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(
    searchParams.get("pageSize") ?? String(DEFAULT_PAGE_SIZE),
    10
  );
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Custom hooks
  const { modalState, openModal, closeModal } = useModalState();
  const { form, createNewForm } = useFormManagement();

  // API hooks
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", x_child],
    queryFn: () => SchemaService.getColumnGridData(x_child ?? ""),
    enabled: !!x_child,
  });

  const { data: schemaInfo } = useQuery({
    queryKey: ["schema", x_child],
    queryFn: () => SchemaService.getSchemaByName(x_child),
    enabled: !!x_child,
  });

  // Computed values
  const total = useMemo(() => tableData.length, [tableData]);
  const schemaJson = useMemo(() => parseSchemaJson(schemaInfo), [schemaInfo]);
  const headerTitle = useMemo(
    () => `Selected ${capitalizeText(x_child ?? "")} List`,
    [x_child]
  );

  // Callbacks
  const updateTableData = useCallback(
    (newData: TableRecord[]) => {
      setTableData(newData);
      onChange?.(newData);
      onTableDataChange?.(newData);
    },
    [setTableData, onChange, onTableDataChange]
  );

  const { handleSave, handleUpdate, handleDelete, handleBulkDelete } =
    useTableOperations(
      tableData,
      updateTableData,
      closeModal,
      createNewForm,
      x_child
    );

  const handleEdit = useCallback(
    (index: string) => {
      const record = tableData.find(item => item.index === index);
      if (record) {
        createNewForm(record);
        openModal(index);
      }
    },
    [tableData, createNewForm, openModal]
  );

  const handleAddNew = useCallback(() => {
    createNewForm();
    openModal();
  }, [createNewForm, openModal]);

  const handlePageChange = useCallback((newPage: number, newSize?: number) => {
    setPage(newPage);
    setPageSize(newSize ?? DEFAULT_PAGE_SIZE);
  }, []);

  const handleBulkDeleteWithState = useCallback(async () => {
    setIsBulkDeleting(true);
    try {
      await handleBulkDelete(selectedRowKeys);
      setSelectedRowKeys([]);
    } finally {
      setIsBulkDeleting(false);
    }
  }, [handleBulkDelete, selectedRowKeys]);

  // Memoized components
  const columns: ProColumns<TableRecord>[] = useMemo(() => {
    const dynamicColumns: ProColumns<TableRecord>[] =
      columnGridData?.grid?.map(
        (field: { field_title: string; field_name: string }) => ({
          title: field.field_title,
          dataIndex: field.field_name.toLowerCase(),
          key: field.field_name.toLowerCase(),
        })
      ) ?? [];

    return [
      ...dynamicColumns,
      {
        title: "Actions",
        width: 100,
        valueType: "option",
        key: "actions",
        render: (_: any, record: TableRecord) => [
          <CustomButton
            key="edit"
            icon={<Edit2 size={16} />}
            type="text"
            onClick={() => handleEdit(record.index)}
            title="Edit"
          />,
          <Popconfirm
            key="delete"
            title="Are you sure you want to delete this item?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => handleDelete(record.index)}
          >
            <CustomButton
              danger
              icon={<Trash size={16} />}
              type="text"
              title="Delete"
            />
          </Popconfirm>,
        ],
      },
    ];
  }, [columnGridData, handleEdit, handleDelete]);

  const menuItems: MenuProps["items"] = useMemo(() => {
    if (selectedRowKeys.length === 0) {
      return [];
    }

    return [
      {
        key: "delete",
        label: (
          <Popconfirm
            title={`Are you sure you want to delete ${selectedRowKeys.length} selected item(s)?`}
            onConfirm={handleBulkDeleteWithState}
            okText="Yes"
            cancelText="No"
          >
            <span className="flex items-center gap-2 text-sm text-red-500">
              <Trash size={16} />
              {isBulkDeleting
                ? "Deleting..."
                : `Delete (${selectedRowKeys.length})`}
            </span>
          </Popconfirm>
        ),
      },
    ];
  }, [selectedRowKeys.length, handleBulkDeleteWithState, isBulkDeleting]);

  const toolBarRender = useCallback(() => {
    const toolbarItems = [
      <CustomButton
        key="add"
        icon={<Plus size={16} />}
        onClick={handleAddNew}
        title="Add New Record"
      >
        Add New
      </CustomButton>,
    ];

    if (selectedRowKeys.length > 0) {
      toolbarItems.push(
        <Dropdown key="actions" menu={{ items: menuItems }}>
          <CustomButton
            title={`Bulk Actions (${selectedRowKeys.length} selected)`}
          >
            Actions ({selectedRowKeys.length})
          </CustomButton>
        </Dropdown>
      );
    }

    return toolbarItems;
  }, [handleAddNew, menuItems, selectedRowKeys.length]);

  const handleModalOk = useCallback(() => {
    if (modalState.isEditMode && modalState.editingId) {
      handleUpdate(form, modalState.editingId);
    } else {
      handleSave(form);
    }
  }, [
    modalState.isEditMode,
    modalState.editingId,
    handleUpdate,
    handleSave,
    form,
  ]);

  return (
    <>
      <ChildTable<TableRecord>
        columns={columns}
        dataSource={tableData}
        loading={!tableData}
        page={page}
        pageSize={pageSize}
        total={total}
        rowKey="index"
        headerTitle={headerTitle}
        toolBarRender={toolBarRender}
        onPageChange={handlePageChange}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />

      <CommonModal
        open={modalState.open}
        title={modalState.isEditMode ? "Edit Item" : "Add New Item"}
        width={MODAL_WIDTH}
        okText={modalState.isEditMode ? "Update" : "Save"}
        cancelText="Cancel"
        onOk={handleModalOk}
        onCancel={closeModal}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        key={`${modalState.isEditMode ? "edit" : "add"}-${modalState.editingId ?? "new"}`}
      >
        {schemaJson ? (
          <SchemaFormView
            key={`form-${modalState.isEditMode ? "edit" : "add"}-${modalState.editingId ?? "new"}`}
            schemaJson={schemaJson}
            form={form}
          />
        ) : (
          <LoadingSpinner />
        )}
      </CommonModal>
    </>
  );
};
